import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { generateBoardReport } from '../generateReport';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Scale, ShieldCheck } from 'lucide-react';

// --- ANIMATION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  show: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", duration: 0.3 } },
  exit: { opacity: 0, scale: 0.95, y: -10, transition: { duration: 0.2 } }
};

export default function ProgressTracker() {
  const { token, user, isImpersonating } = useAuth();
  const navigate = useNavigate();
  
  const [tasks, setTasks] = useState([]);
  const [telemetryState, setTelemetryState] = useState(null); // Tracks active firewall defenses
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [downloadingEvidence, setDownloadingEvidence] = useState(null);

  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });
  const [previewModal, setPreviewModal] = useState({ show: false, url: '', name: '', type: '' });

  useEffect(() => {
    if (user?.org_id) {
      fetchTasks();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.org_id, token]);

  const fetchTasks = async () => {
    if (!user || !user.org_id) return;
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const reqConfig = { headers: { Authorization: `Bearer ${token}` }, params: { orgId: user.org_id } };
      
      const [progressRes, invRes, anaRes] = await Promise.all([
        axios.get(`${baseURL}/api/progress`, reqConfig),
        axios.get(`${baseURL}/api/inventory`, reqConfig).catch(() => ({data: []})),
        axios.get(`${baseURL}/api/inventory/analyze`, reqConfig).catch(() => ({data: null}))
      ]);

      const progressData = progressRes.data?.tasks || [];
      const telemetry = progressRes.data?.telemetry || {};
      setTelemetryState(telemetry);

      const inventory = invRes.data || [];
      const analysis = anaRes.data || null;

      const consolidatedTasks = [];
      const progressMap = {};
      
      progressData.forEach(p => {
        const key = p.item_key || p.item_title;
        if (key) progressMap[key] = p;
      });

      // 1. Priority Actions from Cross-Tool Analysis
      if (analysis && analysis.priority_actions) {
        analysis.priority_actions.forEach((action, i) => {
          const itemKey = `action_${action.rank || i + 1}`;
          const p = progressMap[itemKey] || progressMap[action.action];
          
          if (p) {
            consolidatedTasks.push({ ...p, source: 'action_plan' });
            p._processed = true;
          } else {
            consolidatedTasks.push({ 
              id: `virtual_${itemKey}`, 
              item_key: itemKey, 
              item_title: action.action, 
              status: 'Pending', 
              item_type: 'action_plan', 
              tool_name: 'General Governance', 
              virtual: true 
            });
          }
        });
      }

      // 2. Cross-Tool Risks
      if (analysis && analysis.cross_tool_risks) {
        analysis.cross_tool_risks.forEach((risk, i) => {
          const itemKey = `cross_risk_${i}`;
          const p = progressMap[itemKey];
          
          if (p) {
            consolidatedTasks.push({ ...p, source: 'cross_risk' });
            p._processed = true;
          } else {
            consolidatedTasks.push({ 
              id: `virtual_${itemKey}`, 
              item_key: itemKey, 
              item_title: risk.immediate_action, 
              status: 'Pending', 
              item_type: 'cross_risk', 
              tool_name: 'Cross-Tool Interoperability', 
              virtual: true 
            });
          }
        });
      }

      // 3. Tool-Specific Gaps & NEW Legal Framework Violations
      inventory.forEach(tool => {
        // Handle generic compliance gaps
        if (tool.compliance_gaps && Array.isArray(tool.compliance_gaps) && tool.compliance_gaps.length > 0) {
          tool.compliance_gaps.forEach((gap, i) => {
            const itemKey = `gap_${tool.id}_${i}`;
            const p = progressMap[itemKey] || progressMap[gap];
            
            if (p) {
              consolidatedTasks.push({ ...p, source: 'compliance_gap', ai_inventory: { tool_name: tool.tool_name } });
              p._processed = true;
            } else {
              consolidatedTasks.push({ 
                id: `virtual_${itemKey}`, 
                item_key: itemKey, 
                item_title: gap, 
                status: 'Pending', 
                item_type: 'compliance_gap', 
                tool_id: tool.id, 
                tool_name: tool.tool_name, 
                virtual: true 
              });
            }
          });
        }

        // 🌟 CONTINUOUS CONTROL MONITORING (CCM) OVERRIDE 🌟
        if (tool.framework_controls && Array.isArray(tool.framework_controls) && tool.framework_controls.length > 0) {
          tool.framework_controls.forEach((ctrl, i) => {
            if (ctrl.status === 'Failed') {
              const itemKey = `fw_ctrl_${tool.id}_${i}`;
              let p = progressMap[itemKey];
              // Build a clean, professional title for the auditor
              const taskTitle = `${ctrl.framework} [${ctrl.control}]: ${ctrl.reason}`;

              // Auto-Remediation Check: If task mentions Data Processing/Privacy AND Firewall has PII defense active
              let isAutoRemediated = false;
              let evidenceNote = null;
              const reasonLower = ctrl.reason.toLowerCase();
              
              if ((reasonLower.includes('data processing') || reasonLower.includes('privacy') || reasonLower.includes('pii')) && telemetry.pii_defense_active) {
                  isAutoRemediated = true;
                  evidenceNote = `Auto-Verified by FCAIO Firewall Telemetry (Event Hash: ${telemetry.latest_log_id})`;
              } else if (reasonLower.includes('injection') && telemetry.injection_defense_active) {
                  isAutoRemediated = true;
                  evidenceNote = `Auto-Verified by FCAIO Guardian Agent (Event Hash: ${telemetry.latest_log_id})`;
              }

              if (p) {
                // If it's already manually completed, don't overwrite it
                if (isAutoRemediated && p.status !== 'Completed') {
                    p.status = 'Completed';
                    p.notes = evidenceNote;
                    p.is_telemetry_verified = true;
                }
                
                consolidatedTasks.push({ 
                  ...p, 
                  source: 'framework_violation', 
                  framework: ctrl.framework,
                  control: ctrl.control,
                  ai_inventory: { tool_name: tool.tool_name } 
                });
                p._processed = true;
              } else {
                consolidatedTasks.push({ 
                  id: `virtual_${itemKey}`, 
                  item_key: itemKey, 
                  item_title: taskTitle, 
                  status: isAutoRemediated ? 'Completed' : 'Pending', 
                  notes: isAutoRemediated ? evidenceNote : null,
                  is_telemetry_verified: isAutoRemediated,
                  item_type: 'framework_violation', 
                  tool_id: tool.id, 
                  tool_name: tool.tool_name, 
                  framework: ctrl.framework,
                  control: ctrl.control,
                  virtual: true 
                });
              }
            }
          });
        }
      });

      // 4. Any remaining tasks (e.g., custom manual tasks)
      progressData.forEach(p => {
        if (!p._processed) {
          consolidatedTasks.push(p);
        }
      });

      setTasks(consolidatedTasks);
    } catch (err) {
      console.error("Failed to fetch progress tasks", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEvidenceAction = async (task, actionType) => {
    setDownloadingEvidence(task.id);
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.get(`${baseURL}/api/progress/${task.id}/evidence`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const response = await fetch(res.data.url);
      const blob = await response.blob();
      
      if (actionType === 'download') {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = task.evidence_file_name || 'secure_evidence';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        let fileType = blob.type;
        const fileName = task.evidence_file_name || '';
        
        if (fileType === 'application/octet-stream' || !fileType) {
           if (fileName.toLowerCase().endsWith('.pdf')) fileType = 'application/pdf';
           else if (fileName.toLowerCase().match(/\.jpe?g$|\.png$/)) fileType = `image/${fileName.split('.').pop()}`;
           else if (fileName.toLowerCase().endsWith('.txt')) fileType = 'text/plain';
        }
        
        const typedBlob = new Blob([blob], { type: fileType });
        const previewUrl = window.URL.createObjectURL(typedBlob);
        setPreviewModal({ show: true, url: previewUrl, name: fileName || 'Secure Evidence', type: fileType });
      }
    } catch (err) {
      console.error("Failed to retrieve secure document", err);
      setAlertModal({ 
        show: true, 
        title: 'Access Denied', 
        message: 'Could not decrypt or locate the secure evidence file.', 
        type: 'error' 
      });
    } finally {
      setDownloadingEvidence(null);
    }
  };

  const closePreviewModal = () => {
    if (previewModal.url) {
      window.URL.revokeObjectURL(previewModal.url); 
    }
    setPreviewModal({ show: false, url: '', name: '', type: '' });
  };

  const handleGenerateReport = async () => {
    if (user?.tier === 0) {
      setAlertModal({ 
        show: true, 
        title: '⭐ Premium Feature Unlocked at Tier 1', 
        message: 'Generating Executive Board Reports with real-time financial exposure and Gemini board summaries is a premium feature.', 
        type: 'info' 
      });
      return;
    }

    setGeneratingReport(true);
    try {
      await generateBoardReport(token, user);
    } catch (err) {
      setAlertModal({ 
        show: true, 
        title: 'Error', 
        message: 'Failed to generate PDF document.', 
        type: 'error' 
      });
    }
    setGeneratingReport(false);
  };

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'Completed').length;
  const progressPercent = totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);

  const STATUS_STYLES = {
    'Completed':   'bg-green-100 text-green-800 border-green-200',
    'In Progress': 'bg-blue-100 text-blue-800 border-blue-200',
    'Waived':      'bg-slate-100 text-slate-700 border-slate-300',
    'Pending':     'bg-white text-slate-500 border-slate-200'
  };

  // 🌟 THE FIX: Added Legal & Framework Violations to the primary grouping 🌟
  const groupedTasks = {
    '⚖️ Legal & Framework Violations (Audit Ready)': tasks.filter(t => t.item_type === 'framework_violation' || t.source === 'framework_violation'),
    '🔥 Priority Action Plan': tasks.filter(t => t.item_type === 'action_plan' || t.source === 'action_plan'),
    '⚠️ Critical Governance Gaps (Overview & Cross-Risks)': tasks.filter(t => t.item_type === 'cross_risk' || t.source === 'cross_risk'),
    '📋 Tool Scorecards Gaps': tasks.filter(t => t.item_type === 'compliance_gap' || t.source === 'compliance_gap'),
    '📌 Other Remediation': tasks.filter(t => !['action_plan', 'cross_risk', 'compliance_gap', 'framework_violation'].includes(t.item_type) && !['action_plan', 'cross_risk', 'compliance_gap', 'framework_violation'].includes(t.source))
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] w-full">
      <motion.div 
        animate={{ scale: [1, 1.1, 1], rotate: [0, 90, 180, 270, 360] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        className="w-16 h-16 bg-gradient-to-tr from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 mb-6"
      >
        <Activity className="w-8 h-8 text-white" />
      </motion.div>
      <h3 className="text-xl font-bold text-slate-800 tracking-tight">Loading Progress...</h3>
      <p className="text-slate-500 text-sm mt-2">Aggregating compliance gaps and remediation tasks.</p>
    </div>
  );

  return (
    <div className="p-8 relative">
      
      {/* ── PREVIEW MODAL ── */}
      <AnimatePresence>
        {previewModal.show && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm"
          >
            <motion.div 
              variants={modalVariants} 
              initial="hidden" 
              animate="show" 
              exit="exit" 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-800 truncate pr-4 flex items-center gap-2">
                  <span>📄</span> {previewModal.name}
                </h3>
                <button 
                  onClick={closePreviewModal} 
                  className="text-slate-500 hover:text-red-600 font-bold px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1 shadow-sm border border-transparent hover:border-red-200"
                >
                  ✕ Close Preview
                </button>
              </div>
              <div className="flex-1 bg-slate-200 w-full h-full p-4">
                {previewModal.type && previewModal.type.startsWith('image/') ? (
                  <div className="w-full h-full flex items-center justify-center bg-white rounded-xl shadow-sm overflow-auto p-4 border border-slate-300">
                    <img src={previewModal.url} alt={previewModal.name} className="max-w-full max-h-full object-contain" />
                  </div>
                ) : (
                  <iframe src={previewModal.url} className="w-full h-full bg-white rounded-xl border border-slate-300 shadow-sm" title="Secure Evidence Preview" />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── ALERT MODAL ── */}
      <AnimatePresence>
        {alertModal.show && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          >
            <motion.div 
              variants={modalVariants} 
              initial="hidden" 
              animate="show" 
              exit="exit" 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 transform transition-all text-center"
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 ${
                alertModal.type === 'success' ? 'bg-emerald-100 text-emerald-600' : 
                alertModal.type === 'info' ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'
              }`}>
                {alertModal.type === 'success' ? '✓' : alertModal.type === 'info' ? 'ℹ' : '⚠'}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{alertModal.title}</h3>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">{alertModal.message}</p>
              
              <div className="flex flex-col gap-2">
                {alertModal.title.includes('Premium') && (
                  <button 
                    onClick={() => {
                      setAlertModal({ show: false, title: '', message: '', type: 'info' });
                      navigate('/upgrade');
                    }} 
                    className="w-full px-4 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                  >
                    Upgrade to Tier 1
                  </button>
                )}
                
                <button 
                  onClick={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })} 
                  className="w-full px-4 py-2.5 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-colors"
                >
                  {alertModal.title.includes('Premium') ? 'Maybe Later' : 'Acknowledge'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── HEADER ── */}
      <div className="flex justify-between items-end mb-8">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Compliance Progress Tracker</h2>
          <p className="text-slate-500 mt-1">
            {telemetryState?.total_events > 0 
                ? `⚡ Continuous Control Monitoring Active: Tracking ${telemetryState.total_events} SecOps Events.` 
                : "Read-Only Executive Dashboard for Remediation Tracking."}
          </p>
        </div>
        
        <motion.button 
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleGenerateReport}
          disabled={generatingReport || isImpersonating}
          className={`font-bold px-5 py-2.5 rounded-lg transition shadow-sm flex items-center gap-2 ${
            user?.tier === 0 
              ? 'bg-slate-100 text-slate-500 border border-slate-300 hover:bg-slate-200' 
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          {generatingReport ? '⏳ Generating...' : (
            <>{user?.tier === 0 ? '⭐' : '📄'} Executive Board Report</>
          )}
        </motion.button>
      </div>

      {/* ── MASTER PROGRESS BAR ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-8">
        <div className="flex justify-between text-sm font-bold text-slate-700 mb-2">
          <span>Overall Corporate Remediation</span>
          <span>{progressPercent}% Complete</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden shadow-inner">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="bg-emerald-500 h-4 rounded-full" 
          />
        </div>
      </div>

      {/* ── TASK LISTS ── */}
      {tasks.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-16 text-center">
          <h3 className="text-xl font-bold text-slate-800 mb-2">No Active Tasks</h3>
          <p className="text-slate-500">Run a Deep Analysis from the AI Inventory to generate action plans.</p>
        </div>
      ) : (
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-10">
          {Object.entries(groupedTasks).map(([groupName, groupTasks]) => {
            if (groupTasks.length === 0) return null;
            return (
              <div key={groupName}>
                <h3 className={`text-lg font-bold mb-4 border-b pb-2 flex items-center gap-2 ${groupName.includes('Legal') ? 'text-purple-800 border-purple-200' : 'text-slate-800 border-slate-200'}`}>
                  {groupName}
                </h3>
                
                <div className="space-y-4">
                  {groupTasks.map(task => {
                    const isCompleted = task.status === 'Completed';
                    const isTelemetryVerified = task.is_telemetry_verified;
                    const taskName = task.item_title || task.task_title;
                    const toolName = task.ai_inventory?.tool_name || task.tool_name || 'General Governance';
                    const statusStyle = STATUS_STYLES[task.status] || STATUS_STYLES['Pending'];

                    return (
                      <motion.div 
                        variants={itemVariants} 
                        key={task.id} 
                        className={`bg-white rounded-2xl border shadow-sm p-6 flex flex-col transition-colors ${
                          isCompleted ? (isTelemetryVerified ? 'border-emerald-300 bg-emerald-50/50' : 'border-green-200 bg-green-50/30') : 
                          'border-slate-200'
                        }`}
                      >
                        <div className="flex justify-between items-start w-full">
                          <div className="flex-1 mr-6">
                            <div className="flex items-center gap-3 mb-3">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200">
                                {toolName}
                              </span>
                              
                              {/* 🌟 NEW: Render the specific legal framework badge if applicable */}
                              {task.framework && (
                                <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-200 shadow-sm">
                                  <Scale className="w-3 h-3" /> {task.framework}
                                </span>
                              )}
                              
                              {/* TELEMETRY BADGE */}
                              {isTelemetryVerified ? (
                                <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border shadow-sm bg-emerald-100 text-emerald-800 border-emerald-300">
                                  <ShieldCheck className="w-3 h-3" /> Verified by Telemetry
                                </span>
                              ) : (
                                <span className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border shadow-sm ${statusStyle}`}>
                                  {task.status || 'Pending'}
                                </span>
                              )}
                            </div>
                            
                            <h4 className={`text-base font-bold ${isCompleted ? 'text-slate-500 line-through opacity-80' : 'text-slate-900'}`}>
                              {taskName}
                            </h4>
                          </div>
                          
                          <div className="flex-shrink-0 flex items-center gap-2">
                            {task.evidence_url && (
                              <>
                                <motion.button 
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => handleEvidenceAction(task, 'preview')}
                                  disabled={downloadingEvidence === task.id}
                                  className="text-[11px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg border border-blue-200 flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm"
                                  title="Preview evidence document"
                                >
                                  {downloadingEvidence === task.id ? '⏳' : '👁 Preview'}
                                </motion.button>
                                <motion.button 
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => handleEvidenceAction(task, 'download')}
                                  disabled={downloadingEvidence === task.id}
                                  className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-lg border border-emerald-200 flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm"
                                  title={`Download ${task.evidence_file_name}`}
                                >
                                  {downloadingEvidence === task.id ? '⏳' : '📥 Download'}
                                </motion.button>
                              </>
                            )}
                          </div>
                        </div>

                        {(task.evidence || task.notes) && (
                          <div className={`mt-4 border rounded-lg p-4 text-sm ${isTelemetryVerified ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-slate-50 border-slate-100 text-slate-600'}`}>
                            {task.evidence && <div className="mb-2">🔗 <strong>Reference:</strong> {task.evidence}</div>}
                            {task.notes && <div className="text-xs font-semibold">⚡ {task.notes}</div>}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}